import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';

export interface SettingsHelpSection {
  heading?: string;
  body: ReactNode;
}

export interface SettingsFieldHelpPopoverProps {
  panelTitle: string;
  ariaLabel: string;
  intro?: ReactNode;
  sections?: SettingsHelpSection[];
  /** Panel opens to the left when near the right edge of a grid (e.g. third column). */
  align?: 'left' | 'right';
}

export default function SettingsFieldHelpPopover({
  panelTitle,
  ariaLabel,
  intro,
  sections = [],
  align = 'left',
}: SettingsFieldHelpPopoverProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const panelPosition =
    align === 'right' ? 'right-0 left-auto' : 'left-0 right-auto';

  return (
    <div ref={rootRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center p-0.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        title={ariaLabel}
      >
        <HelpCircle className="w-4 h-4" aria-hidden />
        <span className="sr-only">{ariaLabel}</span>
      </button>

      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label={ariaLabel}
          className={`absolute ${panelPosition} top-full z-[120] mt-1.5 w-[min(22rem,calc(100vw-2.5rem))] rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl p-3.5 sm:p-4`}
        >
          <p className="text-xs font-semibold text-gray-900 dark:text-white mb-2">{panelTitle}</p>
          {intro ? (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">{intro}</p>
          ) : null}
          {sections.length > 0 ? (
            <ul className="space-y-2.5">
              {sections.map((section, idx) => (
                <li key={section.heading ?? idx}>
                  {section.heading ? (
                    <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                      {section.heading}
                    </span>
                  ) : null}
                  <div
                    className={`text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed ${
                      section.heading ? 'mt-0.5' : ''
                    }`}
                  >
                    {section.body}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
